// Admin Users Page
import React, { useEffect, useState } from 'react';
import { apiRequest } from '@/lib/supabase';
import { formatRelativeTime } from '@/lib/utils';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { ActionLinkModal } from '@/components/shared/ActionLinkModal';
import { useToast } from '@/components/shared/ToastProvider';
import { useCurrentUser } from '@/context/PersonaProvider';
import { Plus, Trash2, Shield, KeyRound, Mail, RotateCcw } from 'lucide-react';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'super_admin' | 'admin' | 'viewer' | 'member' | 'billing';
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  mfa_enabled: boolean;
}

const ROLE_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  super_admin: { bg: 'var(--ps-pill-purple-bg)', text: 'var(--ps-pill-purple-tx)', label: 'Super Admin' },
  admin: { bg: 'var(--ps-pill-blue-bg)', text: 'var(--ps-pill-blue-tx)', label: 'Admin' },
  member: { bg: 'var(--ps-pill-blue-bg)', text: 'var(--ps-pill-blue-tx)', label: 'Member' },
  billing: { bg: 'var(--ps-pill-green-bg)', text: 'var(--ps-pill-green-tx)', label: 'Billing' },
  viewer: { bg: 'var(--ps-pill-gray-bg)', text: 'var(--ps-pill-gray-tx)', label: 'Read Only' },
};

const ROLE_PERMISSIONS: Record<string, string[]> = {
  super_admin: ['All permissions including user management'],
  admin: ['Manage licenses, orgs, billing, API keys'],
  member: ['Standard account access without administration'],
  billing: ['View and manage billing only'],
  viewer: ['Read-only access to all resources'],
};

export function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'member' as AdminUser['role'] });
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [deactivateTarget, setDeactivateTarget] = useState<{ id: string; email: string } | null>(null);
  const [mfaResetTarget, setMfaResetTarget] = useState<{ id: string; email: string } | null>(null);
  const [actionLink, setActionLink] = useState<{ title: string; link: string } | null>(null);
  const { toast } = useToast();
  // Only a super_admin may grant/act-on the super_admin role (mirrors the
  // server-side gate in user.routes.ts). Non-super admins never see the option.
  // AdminUsers is always mounted under PersonaProvider in the app; guard the read
  // so a bare render (e.g. unit tests without the provider) degrades to "not a
  // super admin" — which safely hides the elevated controls — instead of throwing.
  const currentUser = useCurrentUser();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  useEffect(() => {
    apiRequest<{ data: AdminUser[] }>('/users')
      .then(response => setUsers(Array.isArray(response.data) ? response.data : []))
      .catch((err) => { setUsers([]); toast({ title: err instanceof Error ? err.message : 'Failed to load users' }); })
      .finally(() => setLoading(false));
  }, [toast]);

  const sendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviting(true);
    try {
      const newUser = await apiRequest<AdminUser>('/users/invite', {
        method: 'POST',
        body: JSON.stringify(inviteForm),
      });
      setUsers(prev => [...prev, newUser]);
      setInviteSuccess(`Invitation sent to ${inviteForm.email}`);
      setInviteForm({ email: '', name: '', role: 'member' });
      setShowInvite(false);
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to send invitation' });
    } finally {
      setInviting(false);
      setTimeout(() => setInviteSuccess(''), 4000);
    }
  };

  const changeRole = async (userId: string, role: AdminUser['role']) => {
    try {
      await apiRequest(`/users/${userId}`, { method: 'PATCH', body: JSON.stringify({ role }) });
      // Only reflect the change after the server confirms it (no optimistic drift).
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role } : u));
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to change role' });
    }
  };

  const deactivateUser = async (userId: string, email: string) => {
    try {
      await apiRequest(`/users/${userId}`, { method: 'DELETE' });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: false } : u));
      toast({ kind: 'success', title: `${email} deactivated.` });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to deactivate user' });
    }
  };

  const resetMfa = async (userId: string, email: string) => {
    try {
      await apiRequest(`/users/${userId}/reset-mfa`, { method: 'POST' });
      // Reflect the server truth: every factor is gone, so MFA reads disabled
      // until the user re-enrolls.
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, mfa_enabled: false } : u));
      toast({ kind: 'success', title: `MFA reset for ${email}.` });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to reset MFA' });
    }
  };

  // Email delivery is unreliable until SES production access, so these admin
  // actions return a single-use action link surfaced in a copy-once modal rather
  // than relying on the mailer. The link is never persisted client-side beyond
  // the open modal.
  const sendReset = async (userId: string, email: string) => {
    try {
      const { actionLink: link } = await apiRequest<{ actionLink: string }>(`/users/${userId}/send-reset`, { method: 'POST' });
      setActionLink({ title: `Password reset link for ${email}`, link });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to generate reset link' });
    }
  };

  const resendInvite = async (userId: string, email: string) => {
    try {
      const { actionLink: link } = await apiRequest<{ actionLink: string; mode: 'invite' | 'recovery' }>(`/users/${userId}/resend-invite`, { method: 'POST' });
      setActionLink({ title: `Invite link for ${email}`, link });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to generate invite link' });
    }
  };

  const reactivate = async (userId: string, email: string) => {
    try {
      const updated = await apiRequest<AdminUser>(`/users/${userId}/reactivate`, { method: 'POST' });
      // Reflect the server truth (the ban is lifted → is_active true).
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...updated, is_active: true } : u));
      toast({ kind: 'success', title: `${email} reactivated.` });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to reactivate user' });
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Admin Users</h1>
          <p className="page-subtitle">Manage admin access and permissions</p>
        </div>
        <button onClick={() => setShowInvite(!showInvite)} style={{ background: 'var(--accent)', color: 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Plus size={14} /> Invite User
        </button>
      </div>

      {inviteSuccess && (
        <div style={{ padding: '10px 14px', background: 'var(--ps-pill-green-bg)', border: '1px solid var(--ps-pill-green-bg)', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: 'var(--ps-pill-green-tx)' }}>
          {inviteSuccess}
        </div>
      )}

      {/* Invite form */}
      {showInvite && (
        <div className="card" style={{ marginBottom: '16px', borderLeft: '3px solid var(--accent)' }}>
          <div className="card-header"><span className="card-title">Invite New Admin</span></div>
          <div className="card-body">
            <form onSubmit={sendInvite} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '12px', alignItems: 'end' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '5px' }}>Email *</label>
                <input type="email" value={inviteForm.email} onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))} required placeholder="user@company.com" style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '5px' }}>Name *</label>
                <input type="text" value={inviteForm.name} onChange={e => setInviteForm(p => ({ ...p, name: e.target.value }))} required placeholder="Full name" style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '5px' }}>Role</label>
                <select value={inviteForm.role} onChange={e => setInviteForm(p => ({ ...p, role: e.target.value as AdminUser['role'] }))} style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px', background: 'var(--surface)' }}>
                  <option value="viewer">Read Only</option>
                  <option value="member">Member</option>
                  <option value="billing">Billing</option>
                  <option value="admin">Admin</option>
                  {isSuperAdmin && <option value="super_admin">Super Admin</option>}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button type="submit" disabled={inviting} style={{ padding: '8px 16px', borderRadius: '6px', background: 'var(--accent)', color: 'white', border: 'none', cursor: inviting ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {inviting ? 'Sending...' : 'Send Invite'}
                </button>
                <button type="button" onClick={() => setShowInvite(false)} style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
        {/* User list */}
        <div className="card" style={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                {['User', 'Role', 'MFA', 'Last Login', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>Loading...</td></tr>
              ) : users.map(user => {
                const name = user.name || user.email || 'Unnamed user';
                const roleConfig = ROLE_COLORS[user.role] || ROLE_COLORS.viewer;
                return (
                  <tr key={user.id} style={{ borderBottom: '1px solid var(--border)', opacity: user.is_active ? 1 : 0.5 }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: user.is_active ? 'var(--accent)' : '#9CA3AF', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600, flexShrink: 0 }}>
                          {name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 600 }}>{name}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <select
                        value={user.role}
                        onChange={e => changeRole(user.id, e.target.value as AdminUser['role'])}
                        disabled={user.role === 'super_admin' && !isSuperAdmin}
                        style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, border: `1px solid ${roleConfig.bg}`, background: roleConfig.bg, color: roleConfig.text, cursor: (user.role === 'super_admin' && !isSuperAdmin) ? 'default' : 'pointer' }}
                      >
                        {(() => {
                          const entries = Object.entries(ROLE_COLORS)
                            // The super_admin option only appears for super_admin callers
                            // (or on a row that already is one, so its label still renders).
                            .filter(([r]) => r !== 'super_admin' || isSuperAdmin || user.role === 'super_admin');
                          // Always render an option for the row's current value so a
                          // role outside ROLE_COLORS never produces a controlled-select warning.
                          const hasCurrent = entries.some(([r]) => r === user.role);
                          return (
                            <>
                              {!hasCurrent && <option value={user.role}>{ROLE_COLORS[user.role]?.label ?? user.role}</option>}
                              {entries.map(([r, cfg]) => (
                                <option key={r} value={r}>{cfg.label}</option>
                              ))}
                            </>
                          );
                        })()}
                      </select>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {user.mfa_enabled
                        ? <span style={{ fontSize: '12px', color: '#16A34A', fontWeight: 600 }}>✓ Enabled</span>
                        : <span style={{ fontSize: '12px', color: '#D97706' }}>⚠ Disabled</span>
                      }
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {user.last_login_at ? formatRelativeTime(user.last_login_at) : 'Never'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '4px', background: user.is_active ? 'var(--ps-pill-green-bg)' : 'var(--ps-pill-red-bg)', color: user.is_active ? 'var(--ps-pill-green-tx)' : 'var(--ps-pill-red-tx)', fontWeight: 600 }}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {(() => {
                        // A non-super admin can't act on a super_admin row (matches
                        // the server gate); everyone else may reset MFA / remove.
                        const canAct = user.role !== 'super_admin' || isSuperAdmin;
                        if (!canAct) return null;
                        // Credential actions on an admin target — reset MFA, send-reset,
                        // resend-invite — are gated to super_admin callers: the server
                        // answers 403 for any admin target otherwise (user.routes.ts), so
                        // we never show a button that would only ever 403.
                        const isAdminTarget = user.role === 'admin' || user.role === 'super_admin';
                        const canManageAdmin = !isAdminTarget || isSuperAdmin;
                        return (
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {canManageAdmin && (
                              <button onClick={() => setMfaResetTarget({ id: user.id, email: user.email })} title="Reset MFA" style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--ps-pill-amber-bg)', background: 'var(--ps-pill-amber-bg)', color: 'var(--ps-pill-amber-tx)', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Shield size={12} /> Reset MFA
                              </button>
                            )}
                            {user.is_active && canManageAdmin && (
                              <button onClick={() => sendReset(user.id, user.email)} title="Generate a password reset link" style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <KeyRound size={12} /> Send reset link
                              </button>
                            )}
                            {user.is_active && !user.last_login_at && canManageAdmin && (
                              <button onClick={() => resendInvite(user.id, user.email)} title="Generate an invite link" style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Mail size={12} /> Resend invite
                              </button>
                            )}
                            {user.is_active ? (
                              <button onClick={() => setDeactivateTarget({ id: user.id, email: user.email })} style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--ps-pill-red-bg)', background: 'var(--ps-pill-red-bg)', color: 'var(--ps-pill-red-tx)', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Trash2 size={12} /> Remove
                              </button>
                            ) : (
                              <button onClick={() => reactivate(user.id, user.email)} title="Restore account access" style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--ps-pill-green-bg)', background: 'var(--ps-pill-green-bg)', color: 'var(--ps-pill-green-tx)', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <RotateCcw size={12} /> Reactivate
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Role permissions */}
        <div className="card">
          <div className="card-header"><span className="card-title">Role Permissions</span></div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {Object.entries(ROLE_COLORS).map(([role, config]) => (
              <div key={role} style={{ padding: '10px 12px', borderRadius: '8px', background: 'var(--bg-secondary)', borderLeft: `3px solid ${config.text}` }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: config.text, marginBottom: '4px' }}>{config.label}</div>
                {ROLE_PERMISSIONS[role].map(perm => (
                  <div key={perm} style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>• {perm}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={deactivateTarget !== null}
        title="Deactivate user?"
        body={deactivateTarget ? `Deactivate ${deactivateTarget.email}? They will lose admin access.` : ''}
        confirmLabel="Deactivate"
        danger
        onConfirm={() => {
          if (deactivateTarget) deactivateUser(deactivateTarget.id, deactivateTarget.email);
          setDeactivateTarget(null);
        }}
        onCancel={() => setDeactivateTarget(null)}
      />

      <ConfirmDialog
        open={mfaResetTarget !== null}
        title="Reset MFA?"
        body={mfaResetTarget ? `Remove all multi-factor authenticators for ${mfaResetTarget.email}? They will be prompted to re-enroll on their next sign-in.` : ''}
        confirmLabel="Reset MFA"
        onConfirm={() => {
          if (mfaResetTarget) resetMfa(mfaResetTarget.id, mfaResetTarget.email);
          setMfaResetTarget(null);
        }}
        onCancel={() => setMfaResetTarget(null)}
      />

      <ActionLinkModal
        open={actionLink !== null}
        title={actionLink?.title ?? ''}
        link={actionLink?.link ?? ''}
        onClose={() => setActionLink(null)}
      />
    </div>
  );
}
